import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Clock, CheckCircle, Circle, AlertTriangle, 
  ChevronLeft, ChevronRight, Send, Loader2,
  Bookmark, BookmarkCheck, Maximize, Minimize, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TakeTest = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [visitedQuestions, setVisitedQuestions] = useState(new Set([0]));
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [resumePrompt, setResumePrompt] = useState(null); // {data} if savedProgress found

  // Identify student for per-user progress key
  const studentUser = (() => {
    try { return JSON.parse(localStorage.getItem('studentUser') || '{}'); } catch (e) { return {}; }
  })();
  const studentId = studentUser.id || studentUser.email || 'anon';
  const progressStorageKey = `vidya365:test-progress:${studentId}:${testId}`;

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchTest();
  }, [testId]);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const fetchTest = async () => {
    try {
      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/tests/${testId}/questions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load test');
      }
      
      const data = await response.json();
      
      setTest(data);

      // Check for saved progress
      let savedTimeLeft = data.duration_minutes * 60;
      try {
        const raw = localStorage.getItem(progressStorageKey);
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved && saved.testId === testId && saved.answers) {
            // Offer resume
            setResumePrompt(saved);
            savedTimeLeft = saved.timeLeft ?? savedTimeLeft;
          }
        }
      } catch (e) { /* ignore */ }

      setTimeLeft(savedTimeLeft);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to load test');
      navigate('/student/tests');
    }
  };

  const applyResume = () => {
    if (!resumePrompt) return;
    setAnswers(resumePrompt.answers || {});
    setMarkedForReview(new Set(resumePrompt.markedForReview || []));
    setVisitedQuestions(new Set(resumePrompt.visitedQuestions || [0]));
    setCurrentQuestionIndex(resumePrompt.currentQuestionIndex || 0);
    setResumePrompt(null);
  };

  const discardResumeAndStartFresh = () => {
    try { localStorage.removeItem(progressStorageKey); } catch (e) { /* noop */ }
    setResumePrompt(null);
    if (test) setTimeLeft(test.duration_minutes * 60);
  };

  // Auto-save progress to localStorage whenever answers / marks / index / timeLeft change
  useEffect(() => {
    if (!test || loading || submitting || resumePrompt) return;
    try {
      const payload = {
        testId,
        answers,
        markedForReview: Array.from(markedForReview),
        visitedQuestions: Array.from(visitedQuestions),
        currentQuestionIndex,
        timeLeft,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(progressStorageKey, JSON.stringify(payload));
    } catch (e) { /* ignore quota */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, markedForReview, visitedQuestions, currentQuestionIndex, timeLeft, test, loading, submitting, resumePrompt]);

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const handleMarkForReview = () => {
    const questionId = test.questions[currentQuestionIndex].id;
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleClearResponse = () => {
    const questionId = test.questions[currentQuestionIndex].id;
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[questionId];
      return newAnswers;
    });
  };

  const handleNavigateToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setVisitedQuestions(prev => new Set([...prev, index]));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const getQuestionStatus = (questionId, index) => {
    const isAnswered = answers.hasOwnProperty(questionId);
    const isMarked = markedForReview.has(questionId);
    const isVisited = visitedQuestions.has(index);
    const isCurrent = index === currentQuestionIndex;

    if (isCurrent) return 'current';
    if (isAnswered && isMarked) return 'answered-marked';
    if (isAnswered) return 'answered';
    if (isMarked) return 'marked';
    if (isVisited) return 'not-answered';
    return 'not-visited';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'current':
        return 'bg-red-600 text-white border-red-700';
      case 'answered-marked':
        return 'bg-blue-500 text-white border-blue-600';
      case 'answered':
        return 'bg-green-500 text-white border-green-600';
      case 'marked':
        return 'bg-orange-500 text-white border-orange-600';
      case 'not-answered':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'not-visited':
        return 'bg-white text-gray-600 border-gray-300';
      default:
        return 'bg-white text-gray-600 border-gray-300';
    }
  };

  const handleAutoSubmit = async () => {
    alert('Time is up! Submitting your test automatically.');
    await handleSubmitTest();
  };

  const handleSubmitTest = async () => {
    if (Object.keys(answers).length === 0) {
      alert('Please answer at least one question before submitting.');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('studentToken');
      
      // Convert answers object to array format expected by backend
      const answersArray = test.questions.map((q) => {
        return answers[q.id] !== undefined ? answers[q.id] : null;
      });
      
      // Calculate time taken
      const timeTaken = Math.ceil((test.duration_minutes * 60 - timeLeft) / 60);
      
      const response = await fetch(`${API_URL}/api/tests/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          test_id: testId,
          answers: answersArray,
          time_taken_minutes: timeTaken
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Submission failed');
      }

      // Clear saved progress after successful submission
      try { localStorage.removeItem(progressStorageKey); } catch (e) { /* noop */ }

      // Navigate to results page
      navigate(`/student/test/${testId}/result`, { 
        state: { result } 
      });
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit test. Please try again.');
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const isQuestionAnswered = (questionId) => {
    return answers.hasOwnProperty(questionId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-red-600" size={48} />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Test not found</p>
      </div>
    );
  }

  const currentQuestion = test.questions[currentQuestionIndex];
  const totalQuestions = test.questions.length;
  const answeredCount = getAnsweredCount();

  // Resume prompt modal
  if (resumePrompt) {
    const savedAnsweredCount = Object.keys(resumePrompt.answers || {}).length;
    const savedAt = resumePrompt.savedAt
      ? new Date(resumePrompt.savedAt).toLocaleString()
      : 'earlier';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full" data-testid="resume-prompt">
          <CardHeader className="bg-amber-50">
            <CardTitle className="flex items-center">
              <Bookmark className="mr-2 text-amber-600" size={22} />
              Resume Test?
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-gray-700">
              We found an in-progress session for <strong>{test.title}</strong>.
            </p>
            <div className="bg-gray-50 border rounded p-4 text-sm space-y-1">
              <div><span className="text-gray-600">Answered:</span> <strong>{savedAnsweredCount}/{totalQuestions}</strong></div>
              <div><span className="text-gray-600">Time left:</span> <strong>{formatTime(resumePrompt.timeLeft || 0)}</strong></div>
              <div><span className="text-gray-600">Saved at:</span> <strong>{savedAt}</strong></div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button data-testid="btn-resume" onClick={applyResume} className="flex-1 bg-amber-600 hover:bg-amber-700">
                Continue where I left off
              </Button>
              <Button data-testid="btn-start-fresh" onClick={discardResumeAndStartFresh} variant="outline" className="flex-1">
                Start Fresh
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Bar */}
      <div className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{test.title}</h1>
              <p className="text-sm text-gray-600">{test.subject}</p>
            </div>
            
            <div className="flex items-center space-x-6">
              <Button
                onClick={toggleFullscreen}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1"
              >
                {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                <span className="hidden md:inline ml-1">
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </span>
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-600">Progress</p>
                <p className="font-bold text-lg">
                  {answeredCount}/{totalQuestions}
                </p>
              </div>
              
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                timeLeft < 300 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-red-600 text-white'
              }`}>
                <Clock size={20} />
                <span className="text-2xl font-bold font-mono">{formatTime(timeLeft)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="bg-red-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    Question {currentQuestionIndex + 1} of {totalQuestions}
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-600">Marks:</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-bold text-sm">
                      +{currentQuestion.marks}
                    </span>
                    {currentQuestion.negative_marks > 0 && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-bold text-sm">
                        -{currentQuestion.negative_marks}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-8">
                <div className="mb-8">
                  <p className="text-lg text-gray-900 leading-relaxed">
                    {currentQuestion.question_text}
                  </p>
                </div>

                <div className="space-y-4">
                  {currentQuestion.options.map((option, index) => {
                    const isSelected = answers[currentQuestion.id] === index;
                    const optionLabel = String.fromCharCode(65 + index); // A, B, C, D
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(currentQuestion.id, index)}
                        className={`w-full p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${
                          isSelected
                            ? 'border-red-600 bg-red-50'
                            : 'border-gray-300 hover:border-red-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold ${
                            isSelected
                              ? 'border-red-600 bg-red-600 text-white'
                              : 'border-gray-400 text-gray-600'
                          }`}>
                            {optionLabel}
                          </div>
                          <span className="flex-1 text-gray-900">{option}</span>
                          {isSelected && <CheckCircle className="text-red-600" size={20} />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between mt-6 pt-6 border-t gap-3">
                  <div className="flex gap-2">
                    <Button
                      onClick={handleMarkForReview}
                      variant="outline"
                      className={`${
                        markedForReview.has(currentQuestion.id)
                          ? 'bg-orange-100 border-orange-500 text-orange-700'
                          : 'border-gray-300'
                      }`}
                    >
                      {markedForReview.has(currentQuestion.id) ? (
                        <><BookmarkCheck size={16} className="mr-2" /> Marked</>
                      ) : (
                        <><Bookmark size={16} className="mr-2" /> Mark for Review</>
                      )}
                    </Button>
                    
                    <Button
                      onClick={handleClearResponse}
                      variant="outline"
                      disabled={!answers.hasOwnProperty(currentQuestion.id)}
                      className="border-gray-300"
                    >
                      <XCircle size={16} className="mr-2" />
                      Clear Response
                    </Button>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleNavigateToQuestion(Math.max(0, currentQuestionIndex - 1))}
                      disabled={currentQuestionIndex === 0}
                      variant="outline"
                    >
                      <ChevronLeft className="mr-2" size={16} />
                      Previous
                    </Button>

                    <Button
                      onClick={() => {
                        if (currentQuestionIndex < totalQuestions - 1) {
                          handleNavigateToQuestion(currentQuestionIndex + 1);
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {currentQuestionIndex === totalQuestions - 1 ? 'Review All' : 'Save & Next'}
                      <ChevronRight className="ml-2" size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Question Palette Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-base">Question Palette</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-5 gap-2 mb-6">
                  {test.questions.map((q, idx) => {
                    const status = getQuestionStatus(q.id, idx);
                    const colorClass = getStatusColor(status);
                    
                    return (
                      <button
                        key={q.id}
                        onClick={() => handleNavigateToQuestion(idx)}
                        className={`w-10 h-10 rounded-lg font-bold text-sm transition-all border-2 ${colorClass}`}
                        title={status.replace('-', ' ').toUpperCase()}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="space-y-2 text-xs border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-green-500 border-2 border-green-600 rounded"></div>
                      <span className="text-gray-700">Answered</span>
                    </div>
                    <span className="font-bold">{Object.keys(answers).length - markedForReview.size}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-blue-500 border-2 border-blue-600 rounded"></div>
                      <span className="text-gray-700">Ans + Marked</span>
                    </div>
                    <span className="font-bold">
                      {Array.from(markedForReview).filter(id => answers.hasOwnProperty(id)).length}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-orange-500 border-2 border-orange-600 rounded"></div>
                      <span className="text-gray-700">Marked</span>
                    </div>
                    <span className="font-bold">
                      {Array.from(markedForReview).filter(id => !answers.hasOwnProperty(id)).length}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-red-100 border-2 border-red-300 rounded"></div>
                      <span className="text-gray-700">Not Answered</span>
                    </div>
                    <span className="font-bold">
                      {visitedQuestions.size - Object.keys(answers).length}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-white border-2 border-gray-300 rounded"></div>
                      <span className="text-gray-700">Not Visited</span>
                    </div>
                    <span className="font-bold">
                      {totalQuestions - visitedQuestions.size}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={() => setShowWarning(true)}
                  className="w-full mt-6 bg-green-600 hover:bg-green-700"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={16} />
                      Submit Test
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Submit Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="bg-yellow-50">
              <CardTitle className="flex items-center text-yellow-800">
                <AlertTriangle className="mr-2" size={24} />
                Confirm Submission
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                  <p><strong>Total Questions:</strong> {totalQuestions}</p>
                  <p><strong>Answered:</strong> {answeredCount}</p>
                  <p><strong>Marked for Review:</strong> {markedForReview.size}</p>
                  <p><strong>Not Answered:</strong> {totalQuestions - answeredCount}</p>
                  <p><strong>Time Remaining:</strong> {formatTime(timeLeft)}</p>
                </div>

                {(markedForReview.size > 0 || answeredCount < totalQuestions) && (
                  <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      {markedForReview.size > 0 && (
                        <span className="block mb-1">
                          ⚠️ You have {markedForReview.size} question(s) marked for review.
                        </span>
                      )}
                      {answeredCount < totalQuestions && (
                        <span className="block">
                          ⚠️ You have {totalQuestions - answeredCount} unanswered question(s).
                        </span>
                      )}
                    </p>
                  </div>
                )}

                <p className="text-gray-700">
                  Once submitted, you cannot change your answers. Do you want to proceed?
                </p>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => setShowWarning(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Review Again
                  </Button>
                  <Button
                    onClick={handleSubmitTest}
                    disabled={submitting}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={16} />
                        Submitting...
                      </>
                    ) : (
                      'Yes, Submit'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TakeTest;
